import React, { useMemo, useState, useEffect } from "react";
import {
  formatNumber,
  formatPassiveTimestamp,
  translateAnomalyOrigin,
} from "./passiveLoadHelpers";
import Pagination from "../../../components/Pagination/Pagination";

const ITEMS_PER_PAGE = 10;
const DEFAULT_TIME_ZONE = "America/Argentina/Buenos_Aires";

const InfoIcon = ({ text }) => (
  <span className="metric-info-icon" title={text} aria-label={text}>
    <i className="bi bi-info-circle" />
  </span>
);

const HeaderWithInfo = ({ children, info, className = "" }) => (
  <th className={className}>
    <span className="header-with-info">
      <span>{children}</span>
      <InfoIcon text={info} />
    </span>
  </th>
);

const TitleWithInfo = ({ children, info }) => (
  <span className="title-with-info">
    <span>{children}</span>
    <InfoIcon text={info} />
  </span>
);

const getField = (item, candidates) => {
  for (const c of candidates) {
    if (item?.[c] != null) return item[c];
  }

  return null;
};

const getTimestampValue = (item) => {
  const rawTimestamp =
    getField(item, ["timestamp", "fecha_hora"]) ||
    getField(item, ["date", "created_at"]);

  if (!rawTimestamp) return 0;

  const time = new Date(rawTimestamp).getTime();

  return Number.isFinite(time) ? time : 0;
};

const formatEventTimestamp = (item, timeZone = DEFAULT_TIME_ZONE) => {
  const localTimestamp = getField(item, [
    "local_timestamp",
    "localTimestamp",
    "fecha_hora_local",
    "localDateTime",
  ]);

  if (localTimestamp) return localTimestamp;

  const rawTimestamp =
    getField(item, ["timestamp", "fecha_hora"]) ||
    getField(item, ["date", "created_at"]);

  if (!rawTimestamp) return "-";

  const date = new Date(rawTimestamp);

  if (Number.isNaN(date.getTime())) {
    return formatPassiveTimestamp(rawTimestamp);
  }

  return date.toLocaleString("es-AR", {
    timeZone: timeZone || DEFAULT_TIME_ZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
};

const translateAnomalyLevel = (value) => {
  const normalized = String(value || "").toLowerCase();

  if (normalized === "normal") return "Normal";
  if (normalized === "revisar") return "Revisar";
  if (normalized === "anomalo" || normalized === "anómalo") return "Anómalo";
  if (normalized === "critico" || normalized === "crítico") return "Crítico";

  return value || "-";
};

const PassiveAnomaliesTable = ({ anomalies, data }) => {
  const [page, setPage] = useState(1);

  const resolvedAnomalies =
    anomalies ?? data?.anomalies ?? data?.totals?.anomalies ?? null;

  const timeZone = data?.timeZone || DEFAULT_TIME_ZONE;

  const sortedAnomalies = useMemo(() => {
    if (!Array.isArray(resolvedAnomalies)) return [];

    return [...resolvedAnomalies].sort((a, b) => {
      return getTimestampValue(b) - getTimestampValue(a);
    });
  }, [resolvedAnomalies]);

  const totalAnomalies = sortedAnomalies.length;
  const totalPages = Math.max(1, Math.ceil(totalAnomalies / ITEMS_PER_PAGE));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  if (!sortedAnomalies.length) return null;

  const startIdx = (page - 1) * ITEMS_PER_PAGE;
  const endIdx = Math.min(startIdx + ITEMS_PER_PAGE, totalAnomalies);
  const shownAnomalies = sortedAnomalies.slice(startIdx, endIdx);

  return (
    <div className="passive-load-summary passive-anomalies">
      <h3>
        <TitleWithInfo info="Listado de períodos donde el consumo fuera de horario fue mayor al esperado o presentó un comportamiento fuera de lo habitual.">
          Eventos a revisar
        </TitleWithInfo>
      </h3>

      <div className="passive-summary-table">
        <table>
          <thead>
            <tr>
              <HeaderWithInfo info="Fecha y hora del período analizado.">
                Fecha / hora
              </HeaderWithInfo>

              <HeaderWithInfo
                className="text-right"
                info="Consumo real registrado en ese período."
              >
                Consumo real
              </HeaderWithInfo>

              <HeaderWithInfo
                className="text-right"
                info="Consumo estimado como habitual o esperado para ese período."
              >
                Consumo esperado
              </HeaderWithInfo>

              <HeaderWithInfo
                className="text-right"
                info="Diferencia entre el consumo real y el consumo esperado."
              >
                Diferencia
              </HeaderWithInfo>

              <HeaderWithInfo info="Nivel del evento detectado. Revisar es leve, Anómalo es medio y Crítico es alto.">
                Clasificación
              </HeaderWithInfo>

              <HeaderWithInfo info="Origen del evento detectado.">
                Origen
              </HeaderWithInfo>
            </tr>
          </thead>

          <tbody>
            {shownAnomalies.map((a, i) => {
              const rawTimestamp =
                getField(a, ["timestamp", "fecha_hora"]) ||
                getField(a, ["date", "created_at"]) ||
                "-";

              const ts = formatEventTimestamp(a, timeZone);

              const real = getField(a, [
                "realValue",
                "real_kw",
                "actual_kw",
                "real_energy",
                "real",
              ]);

              const expected = getField(a, [
                "expectedValue",
                "expected_kw",
                "estimated_kw",
                "expected_energy",
                "expected",
              ]);

              const diff = getField(a, [
                "difference",
                "difference_kw",
                "residual",
                "diff",
              ]);

              const classification = translateAnomalyLevel(
                getField(a, ["classification", "clasificacion"]) ?? "-"
              );

              const source = translateAnomalyOrigin(
                getField(a, ["source", "origen"]) ?? "-"
              );

              return (
                <tr key={`${rawTimestamp}-${startIdx + i}`}>
                  <td className="text-left">{ts}</td>

                  <td className="value-number">
                    {real != null ? formatNumber(real) : "-"}
                  </td>

                  <td className="value-number">
                    {expected != null ? formatNumber(expected) : "-"}
                  </td>

                  <td className="value-number">
                    {diff != null ? formatNumber(diff) : "-"}
                  </td>

                  <td className="text-left">{classification}</td>

                  <td className="text-left">{source}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        filteredItems={sortedAnomalies}
        totalItems={totalAnomalies}
        startIdx={startIdx}
        endIdx={endIdx}
        page={page}
        setPage={setPage}
        totalPages={totalPages}
      />
    </div>
  );
};

export default PassiveAnomaliesTable;